/** Thrown for any non-2xx response, carrying the API's error shape. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)bb_csrf=([^;]*)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

/**
 * Same shape as apps/web/src/api/client.ts's `request`, kept as its own
 * copy rather than a shared import — the two applications are independent
 * front ends against the same API, not sharers of a runtime module. Every
 * call goes through the dev/preview proxy (vite.config.ts) or the admin
 * container's nginx (docker/nginx.admin.conf) to `/api`, so this always
 * talks to the same origin the page was served from.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken()

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch {
    throw new ApiError(0, 'network_error', 'Der Server ist nicht erreichbar. Prüfen Sie die Verbindung.')
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: Record<string, string> } })?.error
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
      error?.details,
    )
  }

  return payload as T
}
